import { useState, type ReactNode } from "react";
import { NavLink, Link, Outlet } from "react-router";
import { signOut, useMe, useTicketsChannel } from "../api/client";
import { clearDraft } from "../draft";
import { loadTheme, saveTheme, type ThemeChoice } from "../theme";
import { ErrorBanner } from "./ui";

const navClass = ({ isActive }: { isActive: boolean }): string => `side-link${isActive ? " active" : ""}`;

// Simple line icons, drawn at 18px in the text colour.
const ICON_PATHS: Record<string, ReactNode> = {
  plus: (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="16" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </>
  ),
  alert: (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ),
  list: (
    <>
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </>
  ),
  chart: (
    <>
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </>
  ),
  inbox: (
    <>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  shield: (
    <>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </>
  ),
  sliders: (
    <>
      <line x1="4" y1="21" x2="4" y2="14" />
      <line x1="4" y1="10" x2="4" y2="3" />
      <line x1="12" y1="21" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12" y2="3" />
      <line x1="20" y1="21" x2="20" y2="16" />
      <line x1="20" y1="12" x2="20" y2="3" />
      <line x1="1" y1="14" x2="7" y2="14" />
      <line x1="9" y1="8" x2="15" y2="8" />
      <line x1="17" y1="16" x2="23" y2="16" />
    </>
  ),
  logout: (
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </>
  ),
  menu: (
    <>
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.9" y1="4.9" x2="6.3" y2="6.3" />
      <line x1="17.7" y1="17.7" x2="19.1" y2="19.1" />
      <line x1="4.9" y1="19.1" x2="6.3" y2="17.7" />
      <line x1="17.7" y1="6.3" x2="19.1" y2="4.9" />
    </>
  ),
  moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  monitor: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>
  ),
};

function Icon({ name }: { name: keyof typeof ICON_PATHS }) {
  return (
    <svg className="side-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICON_PATHS[name]}
    </svg>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="side-section">
      <p className="side-title">{title}</p>
      {children}
    </div>
  );
}

const THEMES: { choice: ThemeChoice; label: string; icon: keyof typeof ICON_PATHS }[] = [
  { choice: "light", label: "Light", icon: "sun" },
  { choice: "dark", label: "Dark", icon: "moon" },
  { choice: "auto", label: "Auto", icon: "monitor" },
];

// Auto follows the system setting; the choice is remembered in this browser.
function ThemeSwitch() {
  const [theme, setTheme] = useState<ThemeChoice>(loadTheme);
  return (
    <div className="theme-switch" role="group" aria-label="Theme">
      {THEMES.map((item) => (
        <button
          key={item.choice}
          type="button"
          className={`theme-opt${theme === item.choice ? " active" : ""}`}
          aria-pressed={theme === item.choice}
          title={item.choice === "auto" ? "Follow the system setting" : `${item.label} theme`}
          onClick={() => {
            setTheme(item.choice);
            saveTheme(item.choice);
          }}
        >
          <Icon name={item.icon} />
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function AppShell() {
  const me = useMe();
  const channel = useTicketsChannel();
  const [leaving, setLeaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Nothing is shown until the session is confirmed, so an expired one goes straight to sign-in without flashing the page.
  if (me.isError) {
    return (
      <div className="app">
        <ErrorBanner>We couldn't load your session. Refresh the page to try again.</ErrorBanner>
      </div>
    );
  }
  if (!me.data) {
    return <div className="app" aria-busy="true" />;
  }

  const close = (): void => setOpen(false);

  return (
    <div className="shell">
      <aside className={`sidebar${open ? " open" : ""}`}>
        <div className="side-head">
          <Link
            to="/new/eng"
            className="brand"
            onClick={() => {
              clearDraft();
              close();
            }}
          >
            <img className="brand-logo" src="/logo-dark.svg" alt="Triage Desk, Vestd Engineering" />
          </Link>
          <button type="button" className="side-toggle" aria-label="Menu" aria-expanded={open} aria-controls="side-body" onClick={() => setOpen((current) => !current)}>
            <Icon name="menu" />
          </button>
        </div>

        <div className="side-body" id="side-body">
          <nav aria-label="Main">
            <Section title="Tickets">
              <NavLink
                to="/new/eng"
                className={navClass}
                onClick={() => {
                  clearDraft();
                  close();
                }}
              >
                <Icon name="plus" />
                Raise an engineering ticket
              </NavLink>
              <NavLink to="/new/incident" className={navClass} onClick={close}>
                <Icon name="alert" />
                Report an incident
              </NavLink>
              <NavLink to="/mine" className={navClass} onClick={close}>
                <Icon name="list" />
                My tickets
              </NavLink>
              <NavLink to="/tickets" className={navClass} onClick={close}>
                <Icon name="list" />
                All tickets
              </NavLink>
              <NavLink to="/incidents" className={navClass} onClick={close}>
                <Icon name="inbox" />
                All incidents
              </NavLink>
            </Section>
            <Section title="Insights">
              <NavLink to="/reports" className={navClass} onClick={close}>
                <Icon name="chart" />
                Reports
              </NavLink>
            </Section>
            {me.data.isAdmin && (
              <Section title="Admin">
                <NavLink to="/admin" end className={navClass} onClick={close}>
                  <Icon name="inbox" />
                  Ticket queue
                </NavLink>
                <NavLink to="/admin/responders" className={navClass} onClick={close}>
                  <Icon name="users" />
                  Incident responders
                </NavLink>
                <NavLink to="/admin/lists" className={navClass} onClick={close}>
                  <Icon name="sliders" />
                  Form lists
                </NavLink>
                <NavLink to="/admin/admins" className={navClass} onClick={close}>
                  <Icon name="shield" />
                  Admins
                </NavLink>
              </Section>
            )}
          </nav>

          <div className="side-foot">
            <ThemeSwitch />
            <span className="meta-chip" title={`Posts to ${channel}`}>
              <span className="dot dot-good" />
              <span className="chip-text">Posts to {channel}</span>
            </span>
            {me.data.email && (
              <span className="meta-chip" title={me.data.email}>
                <span className="chip-text">{me.data.email}</span>
              </span>
            )}
            <button
              type="button"
              className="side-link side-logout"
              disabled={leaving}
              onClick={() => {
                setLeaving(true);
                signOut().catch(() => setLeaving(false));
              }}
            >
              <Icon name="logout" />
              Log out
            </button>
          </div>
        </div>
      </aside>
      <div className="shell-main">
        <Outlet />
      </div>
    </div>
  );
}
